import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../supabase';
import { useLocation } from 'react-router-dom';
import { useFingerprint } from '../../hooks/useFingerprint';
import { isMarketingDomain } from '../../config/domains';

const FLUSH_INTERVAL = 5000; // 5 seconds
const RAGE_CLICK_THRESHOLD = 3;
const RAGE_CLICK_TIME_WINDOW = 1000;

const IntentTracker = () => {
  const location = useLocation();
  const { fingerprint: detectedFp, isLoading } = useFingerprint();

  const [fingerprint, setFingerprint] = useState(null);
  const eventBuffer = useRef([]);
  const flushTimer = useRef(null);
  const clickHistory = useRef([]);
  const hasTrackedPricing = useRef(false);
  const hasTracked50 = useRef(false);
  const hasTracked90 = useRef(false);
  const isOptedOut = useRef(false);

  // Expose reset function globally
  useEffect(() => {
    window.resetIntentTracker = () => {
      localStorage.removeItem('intent_fingerprint');
      localStorage.removeItem('intent_legacy_fingerprint');
      localStorage.removeItem('intent_opt_out');
      window.location.reload();
    };
  }, []);

  // 1. Initialize / Get Fingerprint
  useEffect(() => {
    // Only track on marketing domain (public website) or localhost for testing
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isMarketingDomain() && !isLocal) return;

    // Only track on the website homepage
    if (location.pathname !== '/') return;

    // Check opt-out status first
    if (localStorage.getItem('intent_opt_out') === 'true') {
      isOptedOut.current = true;
      console.log('User opted out of Intent Tracking');
      return;
    }

    if (isLoading) return;

    // Use detected fingerprint from hook, or fallback if absolutely necessary
    // Note: useFingerprint handles the actual generation/loading
    let fp = detectedFp;

    // Check for legacy ID migration
    const currentStored = localStorage.getItem('intent_fingerprint');

    if (currentStored && currentStored.startsWith('v-') && currentStored !== fp) {
        // Migration: Save legacy ID
        localStorage.setItem('intent_legacy_fingerprint', currentStored);
    }

    // Fallback: If hook fails or returns null (e.g. adblocker), try to use stored or generate simple
    if (!fp) {
        if (currentStored) {
            fp = currentStored; // Keep using stored if valid
        } else {
            // Generate simple ID as last resort
            fp = 'v-' + crypto.randomUUID();
        }
    }

    if (fp) {
        localStorage.setItem('intent_fingerprint', fp);
        setFingerprint(fp);
    }

  }, [detectedFp, isLoading]);

  // 2. Setup Flush Timer
  useEffect(() => {
    if (!fingerprint || isOptedOut.current || location.pathname !== '/') return;

    flushTimer.current = setInterval(flushEvents, FLUSH_INTERVAL);
    return () => clearInterval(flushTimer.current);
  }, [fingerprint]);

  // 3. Helper: Add Event
  const trackEvent = (type, data = {}) => {
    if (!fingerprint || isOptedOut.current) return;

    // Deduplicate rapid same-events if needed, but for now just push
    eventBuffer.current.push({
      type,
      data,
      url: window.location.href, // Captures hash routes too
      timestamp: Date.now()
    });
  };

  // 4. Flush to Edge Function
  const flushEvents = async () => {
    if (eventBuffer.current.length === 0) return;
    if (!fingerprint || isOptedOut.current) {
        return;
    }

    const eventsToSend = [...eventBuffer.current];
    eventBuffer.current = []; // Clear buffer immediately

    // Get current user if any to track user status (Trial/Paying)
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    try {
      const { data, error } = await supabase.functions.invoke('intent-engine', {
        body: {
          fingerprint,
          events: eventsToSend,
          url: window.location.href,
          referrer: document.referrer,
          user_id: userId
        }
      });

      if (error) {
        // Silently fail for now to avoid console spam on adblockers
        // console.error('Intent Engine Error:', error);
      } else {
        if (data?.trigger_conversion) {
            console.log('🔥 High Intent Detected!');
            // Here we could trigger a global event or update context
        }
      }
    } catch (err) {
      // console.error('Flush Exception:', err);
    }
  };

  // 5. Event Listeners
  useEffect(() => {
    if (!fingerprint || isOptedOut.current || location.pathname !== '/') return;

    // --- Copy Text ---
    const handleCopy = () => {
      const text = window.getSelection().toString();
      if (text && text.length > 5) {
        trackEvent('copy_text', { length: text.length, text_preview: text.substring(0, 20) });
      }
    };

    // --- Scroll Depth ---
    let scrollTimeout = null;
    const handleScroll = () => {
      // Throttle scroll events to ~100ms to prevent main thread blocking
      if (scrollTimeout) return;

      scrollTimeout = setTimeout(() => {
        scrollTimeout = null;

        const winHeight = window.innerHeight;
        const docHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY;
        const pct = Math.round((scrollTop / (docHeight - winHeight)) * 100);

        if (pct > 50 && !hasTracked50.current) {
          trackEvent('scroll_depth_50');
          hasTracked50.current = true;
        }
        if (pct > 90 && !hasTracked90.current) {
          trackEvent('scroll_depth_90');
          hasTracked90.current = true;
        }

        // Check for specific elements (Pricing)
        // This is a bit fragile if IDs change, but standard for Landing Pages
        const pricingEl = document.getElementById('pricing');
        if (pricingEl && !hasTrackedPricing.current) {
            const rect = pricingEl.getBoundingClientRect();
            if (rect.top >= 0 && rect.bottom <= winHeight) {
                trackEvent('view_pricing');
                hasTrackedPricing.current = true;
            }
        }
      }, 100);
    };

    // --- Rage Clicks ---
    const handleClick = (e) => {
        const now = Date.now();
        // Remove old clicks
        clickHistory.current = clickHistory.current.filter(t => now - t < RAGE_CLICK_TIME_WINDOW);
        clickHistory.current.push(now);

        if (clickHistory.current.length >= RAGE_CLICK_THRESHOLD) {
            trackEvent('rage_click', { x: e.clientX, y: e.clientY });
            clickHistory.current = []; // Reset
        }
    };

    // --- Visibility Change ---
    const handleVisibility = () => {
        if (document.hidden) {
            flushEvents(); // Flush when tab hidden
        }
    };

    window.addEventListener('copy', handleCopy);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('click', handleClick);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
        window.removeEventListener('copy', handleCopy);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('click', handleClick);
        document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fingerprint, location.pathname]);

  // Page View Tracking
  useEffect(() => {
      if (fingerprint && !isOptedOut.current && location.pathname === '/') {
        hasTrackedPricing.current = false;
        hasTracked50.current = false;
        hasTracked90.current = false;
        trackEvent('page_view', { path: location.pathname });
      }
  }, [location.pathname, fingerprint]);

  return null; // Headless component
};

export default IntentTracker;
